/**
 * Tenants — list every brokerage and provision new ones (Workstream A).
 *
 * Reads:  GET  /admin/tenants            (includes the billing_plan join)
 * Writes: POST /admin/tenants            (create, PENDING; mirrors to Auth0 Org)
 *         POST /admin/tenants/{id}/activate | /deactivate  (status transitions)
 *
 * super_admin only — the sidebar/route is reachable by any signed-in staff user
 * but the API returns 403 for non-super_admins (surfaced inline).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminService } from "@re-platform/api-client";
import type { BrokerageType, TenantWithPlan } from "@re-platform/api-client";
import { api } from "../../lib/api";
import { Avatar, cardList, cardShell, ExpandSign } from "../../lib/cards";
import { ContactButtons } from "../../lib/contacts";
import { TenantManagePanel } from "./TenantDetail";

type BadgeColor = { bg: string; fg: string };

const MUTED_COLOR: BadgeColor = {
  bg: "var(--rp-color-surface-2)",
  fg: "var(--rp-color-text-muted)",
};

const STATUS_COLORS: Record<string, BadgeColor> = {
  ACTIVE: {
    bg: "var(--rp-color-success-faint)",
    fg: "var(--rp-color-success)",
  },
  PENDING: {
    bg: "var(--rp-color-warning-faint)",
    fg: "var(--rp-color-warning)",
  },
  SUSPENDED: MUTED_COLOR,
  CANCELLED: MUTED_COLOR,
};

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] ?? MUTED_COLOR;
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

/** A small (i) badge that shows a hover popup — more reliable than a native
 * `title` (which lags and is desktop-only). */
function InfoHint({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        aria-label={text}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "1px solid var(--rp-color-border)",
          fontSize: 9,
          fontStyle: "italic",
          cursor: "help",
          userSelect: "none",
        }}
      >
        i
      </span>
      {show && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--rp-color-text)",
            color: "var(--rp-color-surface)",
            fontSize: 11,
            lineHeight: 1.3,
            padding: "4px 8px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            zIndex: 10,
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            fontStyle: "normal",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function CreateTenantForm({ onDone }: { onDone: (msg: string) => void }) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [brokerageType, setBrokerageType] =
    useState<BrokerageType>("REAL_ESTATE");

  const mutation = useMutation({
    mutationFn: () =>
      adminService.createTenant(api, {
        slug: slug.trim(),
        display_name: displayName.trim(),
        brokerage_type: brokerageType,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setSlug("");
      setDisplayName("");
      setBrokerageType("REAL_ESTATE");
      onDone(`Created “${created.display_name}” (PENDING).`);
    },
  });

  const canSubmit =
    slug.trim().length >= 2 &&
    displayName.trim().length >= 1 &&
    !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      style={{
        ...cardShell,
        alignItems: "flex-end",
        gap: 16,
        marginBottom: 20,
      }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span
          style={{
            color: "var(--rp-color-text-muted)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Slug
          <InfoHint text="lowercase letters, digits and hyphens only" />
        </span>
        <input
          value={slug}
          placeholder="acme-realty"
          onChange={(e) => setSlug(e.target.value)}
          style={inputStyle}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>
          Display name
        </span>
        <input
          value={displayName}
          placeholder="Acme Realty"
          onChange={(e) => setDisplayName(e.target.value)}
          style={{ ...inputStyle, width: 220 }}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Type</span>
        <select
          value={brokerageType}
          onChange={(e) => setBrokerageType(e.target.value as BrokerageType)}
          style={inputStyle}
        >
          <option value="REAL_ESTATE">Real estate</option>
          <option value="MORTGAGE">Mortgage</option>
        </select>
      </label>
      <button
        type="submit"
        className="rp-btn rp-btn--primary rp-btn--sm"
        disabled={!canSubmit}
      >
        {mutation.isPending ? "Creating…" : "Create brokerage"}
      </button>
      {mutation.isError && (
        <span style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
          {mutation.error instanceof Error
            ? mutation.error.message
            : "Create failed"}
        </span>
      )}
    </form>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 13,
  width: 180,
};

function StatusAction({
  tenant,
  onDone,
}: {
  tenant: TenantWithPlan;
  onDone: (msg: string) => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: "activate" | "deactivate") =>
      next === "activate"
        ? adminService.activateTenant(api, tenant.id)
        : adminService.deactivateTenant(api, tenant.id),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      onDone(`${tenant.display_name} → ${row.status}.`);
    },
    // Activation is gated on a complete brokerage profile, and the 422 names
    // exactly what is still missing. Without this the button just did nothing:
    // the tenant stayed PENDING and the reason was only in the network tab.
    onError: (err) =>
      onDone(err instanceof Error ? err.message : "Status change failed."),
  });

  // Terminal — nothing to toggle. With no table cell to fill, an em-dash is
  // just noise on the card.
  if (tenant.status === "CANCELLED") return null;
  const activating = tenant.status !== "ACTIVE";
  return (
    <button
      type="button"
      className="rp-btn rp-btn--secondary rp-btn--sm"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate(activating ? "activate" : "deactivate")}
    >
      {mutation.isPending ? "…" : activating ? "Activate" : "Suspend"}
    </button>
  );
}

export function Tenants() {
  const [toast, setToast] = useState<string | null>(null);
  // One open at a time: the panel is a full brokerage profile, and two of them
  // expanded pushes every other tenant off the screen.
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery<TenantWithPlan[]>({
    queryKey: ["tenants"],
    queryFn: () => adminService.listTenants(api),
    staleTime: 30_000,
  });

  function onDone(msg: string) {
    setToast(msg);
    // 8s, not 3: this strip now also carries the activation gate's list of
    // what the profile is still missing, which cannot be read in three.
    setTimeout(() => setToast(null), 8000);
  }

  if (q.isLoading)
    return <p style={{ color: "var(--rp-color-text-muted)" }}>Loading…</p>;
  if (q.isError)
    return <p style={{ color: "var(--rp-color-danger)" }}>{String(q.error)}</p>;

  const tenants = q.data ?? [];

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Tenants</h1>
      {toast && (
        <div
          style={{
            // Neutral, not green: this same strip now carries the activation
            // gate's 422 ("still missing: …"), and a failure announced in the
            // success colour reads as though it worked.
            background: "var(--rp-color-surface-2)",
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 13,
            whiteSpace: "pre-wrap",
          }}
        >
          {toast}
        </div>
      )}

      <CreateTenantForm onDone={onDone} />

      {tenants.length === 0 ? (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          No tenants yet — create the first brokerage above.
        </p>
      ) : (
        <div style={cardList}>
          {tenants.map((t) => (
            <div key={t.id}>
              <div
                className="rp-clientcard"
                style={{
                  ...cardShell,
                  // Square the bottom so the Manage panel reads as part of this
                  // card. One shorthand, not two corner longhands — cardShell
                  // already sets borderRadius, and mixing them makes the result
                  // depend on property order (React warns).
                  borderRadius:
                    openId === t.id
                      ? "var(--rp-radius-lg) var(--rp-radius-lg) 0 0"
                      : "var(--rp-radius-lg)",
                }}
              >
                <Avatar name={t.display_name} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  {/* Plain text, not a link: Manage opens the same thing right
                      here, and two ways into one screen — one of them silently
                      navigating away — is the inconsistency the expand was
                      meant to remove. */}
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--rp-color-text)",
                    }}
                  >
                    {t.display_name}
                  </div>
                  {/* slug, kind, plan and the Auth0 link on one muted line —
                    four columns that were mostly one short word each. The
                    org id stays a hover title: it identifies the org but
                    nobody reads it off the screen.

                    The contact buttons share this WRAPPING row, the same shape
                    Staff and Clients use, so email/phone drop to their own line
                    as the window narrows rather than squeezing the meta. */}
                  <div
                    style={{
                      marginTop: 4,
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--rp-color-text-muted)",
                        fontSize: 12.5,
                      }}
                    >
                      {t.slug}
                      {" · "}
                      {t.brokerage_type === "MORTGAGE"
                        ? "Mortgage"
                        : "Real estate"}
                      {" · "}
                      {t.billing_plan?.plan_type ?? "no plan"}
                      {t.auth0_org_id ? (
                        <span title={t.auth0_org_id}> · Auth0 linked</span>
                      ) : (
                        " · Auth0 not linked"
                      )}
                    </span>
                    {/* Null until the brokerage profile is filled — renders
                        nothing at all rather than an empty pair of buttons. */}
                    <ContactButtons
                      email={t.support_email}
                      phone={t.primary_phone}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginLeft: "auto",
                  }}
                >
                  <StatusBadge status={t.status} />
                  <StatusAction tenant={t} onDone={onDone} />
                  <button
                    type="button"
                    className="rp-btn rp-btn--secondary rp-btn--sm"
                    aria-expanded={openId === t.id}
                    onClick={() => setOpenId(openId === t.id ? null : t.id)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <ExpandSign open={openId === t.id} />
                    Manage
                  </button>
                </div>
              </div>

              {openId === t.id && (
                <div
                  style={{
                    border: "1px solid var(--rp-color-border)",
                    borderTop: "none",
                    borderRadius: "0 0 var(--rp-radius-lg) var(--rp-radius-lg)",
                    background: "var(--rp-color-surface-2)",
                    padding: 14,
                  }}
                >
                  <TenantManagePanel
                    tenantId={t.id}
                    status={t.status}
                    onToast={onDone}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
